import { ref } from '@vue/composition-api'
import { axios } from '@/lib/axios'
import { handleError } from '@/lib/error-handler'
import { startRegistration, startAuthentication } from '@simplewebauthn/browser'
import { Message, MessageBox } from 'element-ui'
import { clientType, getUAInfo } from '@/lib/getDeviceInfo'

const uaInfo = getUAInfo(navigator.userAgent)
const { browser, os, ua, engine, device, cpu } = uaInfo
const deviceVendor = device.vendor
const browserName = browser.name

// get sign in options
export const useSignInRequest = () => {
  const options = ref()
  const loading = ref(false)
  const credId = localStorage.getItem('SSOUserCredId') || ''
  const isAndroid = clientType() === 'Android' ? true : false

  const fetchOptions = async (notSupportRef: any) => {
    try {
      // android can't get options when credId is empty
      if (isAndroid && !credId) {
        MessageBox.alert(
          `<p><strong>Please log in with an alternative method and re-register your passkey.</strong></p><p>It may be due to the fact that you have not registered or cleared browser data.</p>`,
          'No available passkey found',
          {
            type: 'info',
            confirmButtonText: 'OK',
            dangerouslyUseHTMLString: true,
          }
        )
        return
      }

      loading.value = true

      const {
        data: { data },
      } = await axios.get('/passkey/get_options', {
        params: { credential_id: isAndroid ? credId : '', platform: (isAndroid ? 'android' : clientType()) || '' },
      })
      if (!data) return

      const signInRequestData = await startAuthentication(data)
      console.log('sign in request', signInRequestData)
      options.value = signInRequestData
    } catch (e) {
      const errorObj = e as any
      console.log('error', JSON.stringify(e))
      const errorList = ['InvalidStateError', 'NotAllowedError', 'NotSupportedError', 'SecurityError']
      const deviceVendorError = ['RedMi']
      const isApple14Desktop =
        device.type === undefined && browser.name === 'Safari' && browser.major === '14' && os.name === 'Mac OS'

      if (
        ['NotSupportedError'].includes(errorObj.name) ||
        (deviceVendorError.includes(deviceVendor) && browserName === 'Firefox' && ['NotAllowedError'].includes(errorObj.name))
      ) {
        Message.error('The device or browser does not support this feature')
      }

      if (['NotAllowedError'].includes(errorObj.name) && isApple14Desktop) {
        notSupportRef.value?.showDialog()
      }

      if (errorObj.name === 'SecurityError') {
        Message.error('The ERROR_INVALID_RP_ID error occurred')
      }

      if (errorList.includes(errorObj.name)) {
        return console.warn(errorObj.message)
      }
      handleError(e)
    } finally {
      loading.value = false
    }
  }

  return { loading, options, fetchOptions }
}

// sign in response
export const useSignInResponse = () => {
  const loading = ref(false)
  const result = ref()
  const isAndroid = clientType() === 'Android' ? true : false

  const confirmSignIn = async (options: any) => {
    try {
      loading.value = true
      const { data } = await axios.post(`/passkey/login?platform=${(isAndroid ? 'android' : clientType()) || ''}`, options)
      result.value = data.code
      console.log('sign in response', result.value)
    } catch (e) {
      const errorObj = e as any
      if (errorObj && errorObj.response && errorObj.response.data && errorObj.response.data.code === 41000) {
        // 41000: passkey not found
        return MessageBox.alert(
          `<p><strong>Please log in with an alternative method and re-register your passkey.</strong></p><p>Due to the removal of the configured passkey by you.</p>`,
          'No available passkey found',
          {
            type: 'info',
            confirmButtonText: 'OK',
            dangerouslyUseHTMLString: true,
          }
        )
      }
      handleError(e)
    } finally {
      loading.value = false
    }
  }

  return { loading, result, confirmSignIn }
}

// get register options
export const useRegisterRequest = () => {
  const loading = ref(false)
  const options = ref()
  const errorName = ref('')

  const fetchOptions = async (notSupportRef: any) => {
    try {
      loading.value = true
      const {
        data: { data },
      } = await axios.get(`/passkey/create_options`)
      const registerRequestData = await startRegistration(data)
      localStorage.setItem(`SSOUserCredId`, registerRequestData.id || '')
      console.log('register request', registerRequestData)
      options.value = registerRequestData
    } catch (e) {
      const errorObj = e as any
      console.log('error', JSON.stringify(e))
      errorName.value = errorObj.name
      const errorList = ['InvalidStateError', 'NotAllowedError', 'NotSupportedError', 'SecurityError']
      console.log('xxxx', errorObj.name)
      const deviceVendorError = ['RedMi']
      const isApple14Desktop =
        device.type === undefined && browser.name === 'Safari' && browser.major === '14' && os.name === 'Mac OS'

      if (
        ['NotSupportedError'].includes(errorObj.name) ||
        (deviceVendorError.includes(deviceVendor) && browserName === 'Firefox' && ['NotAllowedError'].includes(errorObj.name))
      ) {
        Message.error('The device or browser does not support this feature')
      }

      if (['NotAllowedError'].includes(errorObj.name) && isApple14Desktop) {
        notSupportRef.value?.showDialog()
      }

      if (errorObj.name === 'InvalidStateError') {
        Message.error('The authenticator was previously registered')
      }

      if (errorObj.name === 'SecurityError') {
        Message.error('The ERROR_INVALID_RP_ID error occurred')
      }

      if (errorList.includes(errorObj.name)) {
        return console.warn(errorObj.message)
      }
      handleError(e)
    } finally {
      loading.value = false
    }
  }

  return { loading, options, errorName, fetchOptions }
}

// register response
export const useRegisterResponse = () => {
  const loading = ref(false)
  const result = ref()

  const confirmRegister = async (form: any) => {
    try {
      loading.value = true
      const { keyName, options } = form
      const uaText = `${os.name} - ${browser.name} ${browser.version}` || 'Unknown'
      const {
        data: { data },
      } = await axios.post(`/passkey/register`, { secret_key_name: keyName.trim(), user_agent: uaText, options })
      console.log('register response', data)
      result.value = data
      Message.success('Register successfully')
    } catch (e) {
      handleError(e)
    } finally {
      loading.value = false
    }
  }

  return { loading, result, confirmRegister }
}

// get credential list
export const useCredentialList = () => {
  const loading = ref(false)
  const result = ref()

  const fetchCredentialList = async (user_id: number | string) => {
    try {
      loading.value = true
      const {
        data: { data },
      } = await axios.get(`/passkey/${user_id}/user_public_keys`)
      result.value = data
    } catch (e) {
      handleError(e)
    } finally {
      loading.value = false
    }
  }

  return { loading, result, fetchCredentialList }
}

// delete credential by id
export const useDeleteCredential = () => {
  const loading = ref(false)

  const deleteCredential = async (credential_id: string) => {
    try {
      loading.value = true
      await axios.delete(`/passkey`, { data: { credential_id } })
      Message.success('Delete successfully')
    } catch (e) {
      handleError(e)
    } finally {
      loading.value = false
    }
  }

  return { loading, deleteCredential }
}
